import {
  buildOverviewRowsForLayerSamples,
  buildTerritoryPaneFacts,
  buildTradePaneContent,
  buildZonePaneFacts,
  preferredOverviewRow,
} from "./map-overview-facts.js";
import { buildPointSampleRows } from "./map-hover-facts.js";
import { resolveFishItemIconUrl } from "./map-item-icons.js";
import { mapText, siteText } from "./map-i18n.js";
import { buildLocationBookmarkSection } from "./map-location-bookmark.js";
import { normalizeZoneLootProfile } from "./map-signal-contract.js";
import {
  TRADE_NPC_LAYER_ID,
  isTradeNpcLayerId,
} from "./map-trade-npc-contract.js";
import {
  resolveTradeNpcBadgeImageUrl,
  resolveTradeNpcImageUrl,
  tradeNpcForKey,
} from "./map-trade-summary.js";
import {
  normalizeZoneLootConditionSet,
  normalizeZoneLootProvenanceClaims,
  parseZoneLootRateDisplayBasis,
  parseZoneLootRateDisplayState,
  ZONE_LOOT_RATE_DISPLAY_BASIS_PERCENT,
  ZONE_LOOT_RATE_DISPLAY_BASIS_RAW_WEIGHT,
  ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN,
} from "./map-zone-loot-summary.js";
import {
  calculatorLootRowSortValues,
  sortCalculatorLootRows,
} from "../js/components/calculator-loot-sort.js";
import { buildZoneMaskLineageSection } from "./map-zone-mask-lineage.js";
import {
  calculatorLootItemRateDisplay,
  parseCalculatorLootRateDisplayMode,
} from "../js/components/calculator-loot-rate-presentation.js";
import { parseCalculatorFishingModesV1 } from "../js/components/calculator-fishing-source.js";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function normalizeCatchMethods(value) {
  if (value === undefined || value === null) {
    return [];
  }
  return [...parseCalculatorFishingModesV1(value, "mapInfo.catchMethods")];
}

function normalizePointKind(value) {
  const normalized = trimString(value).toLowerCase();
  if (
    normalized === "bookmark" ||
    normalized === "waypoint" ||
    normalized === "clicked" ||
    normalized === "character"
  ) {
    return normalized;
  }
  return "";
}

function normalizeSelectionLayerSamples(selection) {
  if (!Array.isArray(selection?.layerSamples)) {
    return [];
  }
  return selection.layerSamples.filter((sample) => isPlainObject(sample)).map((sample) => cloneJson(sample));
}

const INFO_WINDOW_TITLE_ICON = "inspect-fill";
const INFO_WINDOW_STATUS_ICON = "information-circle";
const HOTSPOTS_LAYER_ID = "hotspots";
const HOTSPOT_KIND = "hotspot";
const HOTSPOT_INFO_ICON = "fish-fill";
const HOTSPOT_UNSET_STAT_VALUE = "Not set";
const LANDMARK_LAYER_IDS = Object.freeze([
  TRADE_NPC_LAYER_ID,
  "region_nodes",
  "bookmarks",
  "live_events",
]);
const LANDMARK_STATUS_PRESENTATION_BY_KEY = Object.freeze({
  bookmark: { icon: "bookmark", labelKey: "info.status.bookmark" },
  character: { icon: "map-pin", label: "Character" },
  hotspot: { icon: HOTSPOT_INFO_ICON, label: "Hotspot" },
  npc: { icon: "trade-origin", label: "NPC" },
  region_node: { icon: "map-pin", label: "Node" },
  sample: { icon: "date-confirmed", label: "Sample" },
  trade_npc: { icon: "trade-origin", label: "NPC" },
  waypoint: { icon: "map-pin", labelKey: "info.status.waypoint" },
});
const LANDMARK_STATUS_PRESENTATION_BY_LAYER = Object.freeze({
  bookmarks: LANDMARK_STATUS_PRESENTATION_BY_KEY.bookmark,
  live_events: LANDMARK_STATUS_PRESENTATION_BY_KEY.character,
  hotspots: LANDMARK_STATUS_PRESENTATION_BY_KEY.hotspot,
  [TRADE_NPC_LAYER_ID]: LANDMARK_STATUS_PRESENTATION_BY_KEY.trade_npc,
});

function landmarkStatusPresentationLabel(presentation) {
  return trimString(presentation?.label) || mapText(presentation?.labelKey);
}

function landmarkStatusPresentationFromKey(key) {
  return LANDMARK_STATUS_PRESENTATION_BY_KEY[trimString(key).toLowerCase()] || null;
}

function pointKindStatusText(pointKind, pointLabel) {
  const normalizedLabel = trimString(pointLabel);
  switch (normalizePointKind(pointKind)) {
    case "bookmark":
      return normalizedLabel || mapText("info.status.bookmark");
    case "waypoint":
      return normalizedLabel || mapText("info.status.waypoint");
    case "character":
      return normalizedLabel || "Character";
    case "clicked":
      return mapText("info.status.clicked");
    default:
      return mapText("info.status.no_selection");
  }
}

function landmarkStatusPresentationFromSelection(selection, layerSamples) {
  const elementKindPresentation = landmarkStatusPresentationFromKey(selectionTargetElementKind(selection));
  if (elementKindPresentation) {
    return elementKindPresentation;
  }
  for (const sample of layerSamples) {
    for (const target of Array.isArray(sample?.targets) ? sample.targets : []) {
      const targetPresentation = landmarkStatusPresentationFromKey(target?.key);
      if (targetPresentation) {
        return targetPresentation;
      }
    }
  }
  const hotspotSample = layerSamples.find(isHotspotSample);
  if (hotspotSample) {
    return LANDMARK_STATUS_PRESENTATION_BY_KEY.hotspot;
  }
  const landmarkSample = layerSamples.find(isLandmarkSample);
  if (landmarkSample) {
    return (
      LANDMARK_STATUS_PRESENTATION_BY_LAYER[trimString(landmarkSample?.layerId)] ||
      LANDMARK_STATUS_PRESENTATION_BY_KEY.waypoint
    );
  }
  return null;
}

function selectionStatusDescriptor(selection, layerSamples) {
  const landmarkPresentation = landmarkStatusPresentationFromSelection(selection, layerSamples);
  const landmarkStatusText = landmarkStatusPresentationLabel(landmarkPresentation);
  if (landmarkStatusText) {
    return {
      statusIcon: trimString(landmarkPresentation?.icon) || INFO_WINDOW_STATUS_ICON,
      statusText: landmarkStatusText,
    };
  }
  const pointKind = normalizePointKind(selection?.pointKind);
  const targetPointLabel = selectionTargetPointLabel(selection);
  return {
    statusIcon: INFO_WINDOW_STATUS_ICON,
    statusText: pointKindStatusText(pointKind, targetPointLabel || selection?.pointLabel),
  };
}

function selectionDetailsTarget(selection) {
  return isPlainObject(selection?.detailsTarget) ? selection.detailsTarget : null;
}

function selectionTargetElementKind(selection) {
  return trimString(selectionDetailsTarget(selection)?.elementKind).toLowerCase();
}

function selectionTargetPointLabel(selection) {
  return trimString(selectionDetailsTarget(selection)?.pointLabel);
}

function selectionTargetCharacterIdentityKey(selection) {
  if (selectionTargetElementKind(selection) !== "character") {
    return "";
  }
  return trimString(selectionDetailsTarget(selection)?.characterIdentityKey);
}

function titleFromSelection(selection, layerSamples, zoneCatalog, runtimeLayers) {
  const elementKind = selectionTargetElementKind(selection);
  const targetPointLabel = selectionTargetPointLabel(selection);
  const landmarkPresentation = landmarkStatusPresentationFromKey(elementKind);
  if (landmarkPresentation) {
    if (targetPointLabel) {
      return targetPointLabel;
    }
    return landmarkStatusPresentationLabel(landmarkPresentation);
  }
  const pointLabel = trimString(selection?.pointLabel);
  const pointKind = normalizePointKind(selection?.pointKind);
  if (
    (pointKind === "bookmark" || pointKind === "waypoint" || pointKind === "character") &&
    pointLabel
  ) {
    return pointLabel;
  }
  const preferred = preferredOverviewRow(layerSamples, {
    zoneCatalog,
    runtimeLayers,
  });
  if (preferred?.value) {
    return preferred.value;
  }
  if (pointLabel) {
    return pointLabel;
  }
  return mapText("info.window_title");
}

function preferredPaneIdFromSelection(selection) {
  switch (selectionTargetElementKind(selection)) {
    case "character":
      return "character";
    case "hotspot":
      return "hotspot";
    case "npc":
    case "trade_npc":
      return "landmark";
    case "sample":
    case "point":
      return "samples";
    default:
      return "";
  }
}

function paneIdExists(panes, paneId) {
  return Boolean(paneId) && panes.some((pane) => pane.id === paneId);
}

function zoneLootConditionKey(group, index) {
  const explicitKey = trimString(group?.conditionOptionKey);
  if (explicitKey) {
    return explicitKey;
  }
  const slotIdx = Number.parseInt(group?.slotIdx, 10) || index + 1;
  const label = trimString(group?.label) || `slot-${slotIdx}`;
  return `${slotIdx}:${label}`;
}

function clampZoneLootConditionIndex(value, count) {
  const index = Number.parseInt(value, 10);
  if (!Number.isInteger(index) || index < 0 || index >= count) {
    return null;
  }
  return index;
}

function lootRateDisplayModeFromSignals(signals) {
  return parseCalculatorLootRateDisplayMode(
    signals?._map_ui?.windowUi?.settings?.lootRateDisplayMode ?? "normalized",
    "map loot rate display mode",
  );
}

const ZONE_LOOT_SORT_BY_VALUES = Object.freeze([
  "calculator",
  "price",
  "grade",
  "database",
  "evidence",
  "droprate",
  "expected_value",
  "exp",
  "totem_exp",
  "alphabetical",
  "item_id",
]);
const ZONE_LOOT_EXPLICIT_SORT_BY_VALUES = Object.freeze(
  ZONE_LOOT_SORT_BY_VALUES.filter((value) => value !== "calculator"),
);
const ZONE_LOOT_SORT_ORDER_VALUES = Object.freeze(["calculator", "desc", "asc"]);
const ZONE_LOOT_EXPLICIT_SORT_ORDER_VALUES = Object.freeze(["desc", "asc"]);

function normalizeZoneLootSortBy(value) {
  const normalized = trimString(value).toLowerCase();
  return ZONE_LOOT_SORT_BY_VALUES.includes(normalized) ? normalized : "calculator";
}

function normalizeExplicitZoneLootSortBy(value) {
  const normalized = trimString(value).toLowerCase();
  return ZONE_LOOT_EXPLICIT_SORT_BY_VALUES.includes(normalized) ? normalized : "price";
}

function resolveZoneLootSortBy(value) {
  const normalized = normalizeZoneLootSortBy(value);
  return normalized === "calculator" ? "price" : normalized;
}

function zoneLootSortByFromSignals(signals) {
  return resolveZoneLootSortBy(signals?._map_ui?.windowUi?.settings?.zoneLootSortBy);
}

function normalizeZoneLootSortOrder(value) {
  const normalized = trimString(value).toLowerCase();
  return ZONE_LOOT_SORT_ORDER_VALUES.includes(normalized) ? normalized : "calculator";
}

function normalizeExplicitZoneLootSortOrder(value) {
  const normalized = trimString(value).toLowerCase();
  return ZONE_LOOT_EXPLICIT_SORT_ORDER_VALUES.includes(normalized) ? normalized : "desc";
}

function resolveZoneLootSortOrder(value) {
  const normalized = normalizeZoneLootSortOrder(value);
  return normalized === "calculator" ? "desc" : normalized;
}

function zoneLootSortOrderFromSignals(signals) {
  return resolveZoneLootSortOrder(signals?._map_ui?.windowUi?.settings?.zoneLootSortOrder);
}

function zoneLootRateDisplayFields(row, lootRateDisplayMode = "normalized") {
  const mode = parseCalculatorLootRateDisplayMode(
    lootRateDisplayMode,
    "zone loot rate display mode",
  );
  const prefix = mode === "normalized"
    ? "normalized"
    : mode === "denormalized"
      ? "denormalized"
      : "raw";
  const preferredText = trimString(row?.[`${prefix}DropRateText`]);
  const preferredState = parseZoneLootRateDisplayState(
    row?.[`${prefix}DropRateState`],
    `${prefix} drop rate state`,
  );
  const preferredTitle = trimString(row?.[`${prefix}DropRateTitle`]);
  const preferredBasis = trimString(row?.[`${prefix}DropRateBasis`]);
  return {
    dropRateText: preferredText,
    dropRateState: preferredState,
    dropRateTitle: preferredTitle,
    dropRateBasis: preferredBasis
      ? parseZoneLootRateDisplayBasis(preferredBasis)
      : "",
  };
}

function normalizeZoneLootSpeciesRow(row, lootRateDisplayMode = "normalized") {
  const rateFields = zoneLootRateDisplayFields(row, lootRateDisplayMode);
  return {
    ...cloneJson(row),
    ...rateFields,
    catchMethods: normalizeCatchMethods(row?.catchMethods),
  };
}

function sortZoneLootRows(rows, sortBy = "price", sortOrder = "desc") {
  return sortCalculatorLootRows(Array.isArray(rows) ? rows : [], {
    by: normalizeExplicitZoneLootSortBy(sortBy),
    order: normalizeExplicitZoneLootSortOrder(sortOrder),
  });
}

function normalizeZoneLootConditionOptions(
  group,
  lootRateDisplayMode = "normalized",
  zoneLootSortBy = "price",
  zoneLootSortOrder = "desc",
) {
  if (!Array.isArray(group?.conditionOptions)) {
    return [];
  }
  return group.conditionOptions
    .filter((option) => isPlainObject(option))
    .map((option) => {
      const conditions = normalizeZoneLootConditionSet(
        option?.conditions,
        "zoneLoot.conditionOption.conditions",
      );
      const speciesRows = Array.isArray(option?.speciesRows)
        ? sortZoneLootRows(
            option.speciesRows.map((row) => normalizeZoneLootSpeciesRow(
              row,
              lootRateDisplayMode,
            )),
            zoneLootSortBy,
            zoneLootSortOrder,
          )
        : [];
      return {
        conditionId: trimString(option?.conditionId),
        conditions,
        conditionText: trimString(option?.conditionText),
        conditionTooltip: trimString(option?.conditionTooltip),
        ...zoneLootRateDisplayFields(option, lootRateDisplayMode),
        rateClaims: normalizeZoneLootProvenanceClaims(
          option?.rateClaims,
          "zoneLoot.conditionOption.rateClaims",
          "groupRate",
        ),
        presenceClaims: normalizeZoneLootProvenanceClaims(
          option?.presenceClaims,
          "zoneLoot.conditionOption.presenceClaims",
          "groupPresence",
        ),
        active: option?.active === true,
        speciesRows,
      };
    })
    .filter((option) => option.conditionText || option.speciesRows.length > 0);
}

function selectedZoneLootConditionIndex(options, conditionSelection, key) {
  if (!options.length) {
    return -1;
  }
  const explicitIndex = isPlainObject(conditionSelection)
    ? clampZoneLootConditionIndex(conditionSelection[key], options.length)
    : null;
  if (explicitIndex != null) {
    return explicitIndex;
  }
  const activeIndex = options.findIndex((option) => option.active === true);
  if (activeIndex >= 0) {
    return activeIndex;
  }
  return 0;
}

function buildZoneLootGroups(
  summary,
  conditionSelection = {},
  lootRateDisplayMode = "normalized",
  zoneLootSortBy = "price",
  zoneLootSortOrder = "desc",
) {
  const groups = Array.isArray(summary?.groups) ? summary.groups : [];
  const speciesRows = Array.isArray(summary?.speciesRows) ? summary.speciesRows : [];
  if (!groups.length && !speciesRows.length) {
    return [];
  }
  return groups
    .map((group, index) => {
      const parsedSlotIdx = Number.parseInt(group?.slotIdx, 10);
      const slotIdx = Number.isInteger(parsedSlotIdx) && parsedSlotIdx >= 0
        ? parsedSlotIdx
        : index + 1;
      const groupLabel = trimString(group?.label);
      const groupRateFields = zoneLootRateDisplayFields(group, lootRateDisplayMode);
      const conditionOptions = normalizeZoneLootConditionOptions(
        group,
        lootRateDisplayMode,
        zoneLootSortBy,
        zoneLootSortOrder,
      );
      const conditionOptionKey = zoneLootConditionKey(group, index);
      const conditionOptionIndex = selectedZoneLootConditionIndex(
        conditionOptions,
        conditionSelection,
        conditionOptionKey,
      );
      const selectedCondition =
        conditionOptionIndex >= 0 ? conditionOptions[conditionOptionIndex] : null;
      const selectedConditionRows = selectedCondition
        ? sortZoneLootRows(selectedCondition.speciesRows, zoneLootSortBy, zoneLootSortOrder)
        : null;
      const fallbackRows = speciesRows
        .filter((row) => {
          const rowGroupLabel = trimString(row?.groupLabel);
          if (rowGroupLabel && groupLabel && rowGroupLabel === groupLabel) {
            return true;
          }
          return (Number.parseInt(row?.slotIdx, 10) || 0) === slotIdx;
        })
        .map((row) => normalizeZoneLootSpeciesRow(row, lootRateDisplayMode));
      const sortedFallbackRows = sortZoneLootRows(fallbackRows, zoneLootSortBy, zoneLootSortOrder);
      const selectedConditionRateFields = selectedCondition || null;
      const provenanceFields = selectedCondition ?? group;
      return {
        slotIdx,
        label: groupLabel,
        fillColor: trimString(group?.fillColor),
        strokeColor: trimString(group?.strokeColor),
        textColor: trimString(group?.textColor),
        dropRateText:
          trimString(selectedConditionRateFields?.dropRateText) || groupRateFields.dropRateText,
        dropRateState:
          selectedConditionRateFields?.dropRateState || groupRateFields.dropRateState,
        dropRateTitle:
          trimString(selectedConditionRateFields?.dropRateTitle) || groupRateFields.dropRateTitle,
        dropRateBasis:
          trimString(selectedConditionRateFields?.dropRateBasis) || groupRateFields.dropRateBasis,
        rateClaims: cloneJson(provenanceFields?.rateClaims ?? []),
        presenceClaims: cloneJson(provenanceFields?.presenceClaims ?? []),
        conditionText: trimString(selectedCondition?.conditionText) || trimString(group?.conditionText),
        conditionTooltip:
          trimString(selectedCondition?.conditionTooltip) || trimString(group?.conditionTooltip),
        catchMethods: normalizeCatchMethods(group?.catchMethods),
        conditionOptions,
        conditionOptionIndex,
        conditionOptionKey,
        rows: selectedConditionRows || sortedFallbackRows,
      };
    })
    .filter((group) => group.label)
    .map((group, index) => ({ group, index }))
    .sort((left, right) =>
      Number(left.group.slotIdx === 0) - Number(right.group.slotIdx === 0)
      || left.index - right.index)
    .map(({ group }) => group);
}

function zoneLootProfileLabel(profileId) {
  return profileId === "harpoon"
    ? siteText("domain.method.harpoon")
    : siteText("domain.method.rod");
}

function rowMatchesZoneLootProfile(row, profileId) {
  const slotIdx = Number.parseInt(row?.slotIdx, 10);
  if (profileId === "unassigned") {
    return slotIdx === 0;
  }
  if (slotIdx === 0) {
    return false;
  }
  const methods = normalizeCatchMethods(row?.catchMethods);
  if (profileId === "harpoon") {
    return methods.includes("harpoon");
  }
  return methods.length === 0 || methods.includes("rod");
}

function groupMatchesZoneLootProfile(group, profileId) {
  const slotIdx = Number.parseInt(group?.slotIdx, 10);
  if (profileId === "unassigned") {
    return slotIdx === 0;
  }
  if (slotIdx === 0) {
    return false;
  }
  const methods = normalizeCatchMethods(group?.catchMethods);
  if (profileId === "harpoon") {
    return methods.includes("harpoon");
  }
  return methods.length === 0 || methods.includes("rod");
}

function buildZoneLootProfiles(groups) {
  const profiles = [];

  for (const profile of [
    { id: "rod", label: zoneLootProfileLabel("rod"), icon: "fishing-mode-rod" },
    { id: "harpoon", label: zoneLootProfileLabel("harpoon"), icon: "fishing-mode-harpoon" },
    { id: "unassigned", label: siteText("domain.loot_group.unassigned"), icon: "question-mark" },
  ]) {
    const profileGroups = groups
      .map((group) => ({
        ...cloneJson(group),
        rows: (Array.isArray(group?.rows) ? group.rows : [])
          .filter((row) => rowMatchesZoneLootProfile(row, profile.id)),
      }))
      .filter(
        (group) =>
          groupMatchesZoneLootProfile(group, profile.id) && group.rows.length > 0,
      );
    if (profileGroups.length) {
      profiles.push({ ...profile, groups: profileGroups });
    }
  }

  return profiles;
}

function selectedZoneLootProfile(profiles, requestedProfile) {
  const profileId = normalizeZoneLootProfile(requestedProfile);
  return profiles.find((profile) => profile.id === profileId)
    || profiles.find((profile) => profile.id === "rod")
    || profiles[0]
    || null;
}

function buildZoneLootSection(
  summary,
  status,
  conditionSelection = {},
  lootRateDisplayMode = "normalized",
  zoneLootSortBy = "price",
  zoneLootSortOrder = "desc",
  requestedProfile = "rod",
) {
  const normalizedStatus = trimString(status || "idle");
  if (normalizedStatus === "idle" && !isPlainObject(summary)) {
    return null;
  }
  const groups = buildZoneLootGroups(
    summary,
    conditionSelection,
    lootRateDisplayMode,
    zoneLootSortBy,
    zoneLootSortOrder,
  );
  const profiles = buildZoneLootProfiles(groups);
  const activeProfile = selectedZoneLootProfile(profiles, requestedProfile);
  const available = summary?.available === true && profiles.some((profile) => profile.groups.length > 0);
  const statusText =
    normalizedStatus === "loaded"
      ? mapText("info.zone_loot.status.loaded")
      : normalizedStatus === "loading"
        ? mapText("info.zone_loot.status.loading")
        : normalizedStatus === "error"
          ? mapText("info.zone_loot.status.error")
          : normalizedStatus
            ? mapText("info.zone_loot.status.other", { status: normalizedStatus })
            : mapText("info.zone_loot.status.idle");

  return {
    id: "zone-loot",
    kind: "zone-loot",
    title: mapText("info.zone_loot.title"),
    statusText,
    summary: trimString(summary?.profileLabel) || mapText("info.zone_loot.summary.default"),
    dataQualityNote: trimString(summary?.dataQualityNote),
    note: trimString(summary?.note),
    groups,
    profiles,
    activeProfileId: activeProfile?.id || normalizeZoneLootProfile(requestedProfile),
    activeProfile,
    available,
  };
}

function buildPointSampleSection(selection, stateBundle, zoneCatalog) {
  const rows = buildPointSampleRows({ source: selection, stateBundle, zoneCatalog });
  if (!rows.length) {
    return null;
  }
  return {
    id: "point-samples",
    kind: "point-samples",
    title: "Ranking Samples",
    rows,
  };
}

function integerValue(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

function modelAssetPath(value) {
  const normalized = trimString(value);
  const match = normalized.match(/(?:^|[\s:])((?:\/models\/|models\/)[^\s"'<>]+\.gl(?:tf|b))/i);
  if (!match) {
    return "";
  }
  const path = match[1].replace(/[),.;]+$/, "");
  return path.startsWith("/") ? path : `/${path}`;
}

function modelPreviewParts(value) {
  const detail = trimString(value).replace(/:\s*(?:\/models\/|models\/).*$/i, "");
  return detail
    .split("/")
    .map((part) => trimString(part))
    .filter(Boolean);
}

function isGullPreviewAsset(value) {
  const parts = modelPreviewParts(value);
  return parts.some((part) => /\b(gull|seagull|bird)\b/i.test(part));
}

function isFishPreviewAsset(value) {
  const path = modelAssetPath(value);
  if (!path || isGullPreviewAsset(value)) {
    return false;
  }
  const parts = modelPreviewParts(value);
  return (
    parts.some((part) => /\bfish\b/i.test(part)) ||
    /\/m018[45]_fish[_/]/i.test(path)
  );
}

function fishPreviewAssetRank(value) {
  const role = modelPreviewParts(value)[0] || "";
  if (/^river-child$/i.test(role)) {
    return 0;
  }
  if (/^sea-child-fish$/i.test(role)) {
    return 2;
  }
  return 1;
}

function preferredFishPreviewAsset(facts) {
  return facts
    .filter((fact) => fact.key === "model_preview_asset" && isFishPreviewAsset(fact.value))
    .map((fact, index) => ({ fact, index, rank: fishPreviewAssetRank(fact.value) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)[0]?.fact || null;
}

function isSeaFishPreviewAsset(value) {
  return isFishPreviewAsset(value) &&
    /__m0184_00_00_00_stand_idle_00__lod0\.gl(?:tf|b)$/i.test(modelAssetPath(value));
}

function preferredHotspotPreviewAsset(facts) {
  return facts.find((fact) => fact.key === "model_preview_asset" && isSeaFishPreviewAsset(fact.value)) ||
    preferredFishPreviewAsset(facts);
}

function preferredSeaFishPreviewAsset(facts) {
  return facts.find((fact) => fact.key === "model_preview_asset" && isSeaFishPreviewAsset(fact.value)) || null;
}

function normalizeHotspotModelTab(value) {
  const key = trimString(value).toLowerCase();
  return key === "fish" || key === "spot" || key === "scene" ? key : "";
}

function buildHotspotModelViewer(facts, selectedModelTab = "") {
  const assets = [];
  const seenPaths = new Set();
  const valueForKey = (key) => facts.find((fact) => fact.key === key)?.value || "";
  const pushAsset = (asset) => {
    const src = modelAssetPath(asset?.src);
    if (!src || seenPaths.has(src)) {
      return;
    }
    seenPaths.add(src);
    const entry = {
      key: trimString(asset?.key) || `${asset?.kind || "model"}:${assets.length}`,
      kind: trimString(asset?.kind) || "preview",
      label: trimString(asset?.label) || `Model ${assets.length + 1}`,
      src,
    };
    if (asset?.animated === false) {
      entry.animated = false;
    }
    for (const key of [
      "shadowIntensity",
      "environmentImage",
      "exposure",
      "cameraOrbit",
      "cameraTarget",
      "fieldOfView",
    ]) {
      const value = trimString(asset?.[key]);
      if (value) {
        entry[key] = value;
      }
    }
    assets.push(entry);
  };

  const seaFishAsset = preferredSeaFishPreviewAsset(facts);
  const hotspotAsset = seaFishAsset || preferredHotspotPreviewAsset(facts);
  const fishAssetPath = modelAssetPath(valueForKey("model_inspection_asset"));
  if (fishAssetPath) {
    pushAsset({
      key: "fish",
      kind: "fish",
      label: "Fish",
      src: fishAssetPath,
      animated: false,
      shadowIntensity: "0",
      environmentImage: "neutral",
      exposure: "1",
      cameraOrbit: "90deg 75deg auto",
    });
  }

  if (hotspotAsset) {
    pushAsset({
      key: "spot",
      kind: "spot",
      label: "Hotspot",
      src: hotspotAsset.value,
    });
  }

  const hotspotId = trimString(valueForKey("hotspot_id"));
  const modelAction = trimString(valueForKey("model_action"));
  const hotspotAction = modelAction
    ? { label: "Hotspot Action", value: modelAction, icon: "cube-view" }
    : null;

  const sceneAssetPath = modelAssetPath(valueForKey("model_scene_asset"));
  if (sceneAssetPath) {
    pushAsset({
      key: "scene",
      kind: "scene",
      label: "Seagulls",
      src: sceneAssetPath,
    });
  }

  const requestedActiveKey = normalizeHotspotModelTab(selectedModelTab);
  const activeKey = assets.some((asset) => asset.key === requestedActiveKey)
    ? requestedActiveKey
    : assets[0]?.key || "";

  return assets.length
    ? {
        assets,
        activeKey,
        ...(hotspotId ? { hotspotId } : {}),
        ...(modelAction ? { modelAction } : {}),
        ...(hotspotAction ? { hotspotAction } : {}),
      }
    : null;
}

function buildHotspotModelFacts(facts) {
  const modelAction = facts.find((fact) => fact.key === "model_action");
  if (!modelAction) {
    return [];
  }
  return [{
    ...modelAction,
    label: "Hotspot Action",
  }];
}

function detailSectionFacts(section) {
  return (Array.isArray(section?.facts) ? section.facts : [])
    .filter((fact) => isPlainObject(fact))
    .map((fact) => ({
      key: trimString(fact.key),
      label: trimString(fact.label),
      value: trimString(fact.value),
      icon: trimString(fact.icon),
    }))
    .filter((fact) => fact.key && fact.value);
}

function firstFactValue(facts, key) {
  return facts.find((fact) => fact.key === key)?.value || "";
}

function hotspotMetric(label, value, icon) {
  const normalizedValue = trimString(value);
  return {
    label,
    value: normalizedValue || HOTSPOT_UNSET_STAT_VALUE,
    icon,
  };
}

function isHotspotSample(sample) {
  return (
    trimString(sample?.layerId) === HOTSPOTS_LAYER_ID ||
    trimString(sample?.kind) === HOTSPOT_KIND
  );
}

function hotspotDetailSection(sample) {
  return (Array.isArray(sample?.detailSections) ? sample.detailSections : [])
    .find((section) => isPlainObject(section) && trimString(section.kind) === "hotspot") || null;
}

function formatHotspotLifeLevel(value) {
  const raw = integerValue(value);
  if (raw == null || raw < 0) {
    return "";
  }
  const levelIndex = raw + 1;
  const tiers = [
    ["Beginner", 10],
    ["Apprentice", 10],
    ["Skilled", 10],
    ["Professional", 10],
    ["Artisan", 10],
    ["Master", 30],
    ["Guru", 100],
  ];
  let remaining = levelIndex;
  for (const [tier, count] of tiers) {
    if (remaining <= count) {
      return `${tier} ${remaining}`;
    }
    remaining -= count;
  }
  return `Guru ${Math.max(1, remaining + 100)}`;
}

function formatHotspotSeconds(value) {
  const ms = integerValue(value);
  if (ms == null || ms < 0) {
    return "";
  }
  const seconds = ms / 1000;
  return `${seconds.toFixed(1).replace(/\.0$/, "")}s`;
}

function formatHotspotAverageSeconds(minValue, maxValue) {
  const minMs = integerValue(minValue);
  const maxMs = integerValue(maxValue);
  if (minMs == null || maxMs == null || minMs < 0 || maxMs < 0) {
    return "";
  }
  return formatHotspotSeconds(Math.round((minMs + maxMs) / 2));
}

function formatHotspotDuration(value) {
  const ms = integerValue(value);
  if (ms == null || ms < 0) {
    return "";
  }
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function hotspotItemRatePresentations(value) {
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate < 0) {
    const unavailable = Object.freeze({
      state: ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN,
      text: "",
      title: "",
    });
    return Object.freeze({
      normalized: unavailable,
      denormalized: unavailable,
      raw: unavailable,
    });
  }
  const normalized = calculatorLootItemRateDisplay({
    state: "known",
    value: rate / 10_000,
    basis: "percent",
  });
  const denormalized = calculatorLootItemRateDisplay(
    {
      state: "known",
      value: rate / 10_000,
      basis: "percent",
    },
    { presentationBasis: "denormalizedWeight" },
  );
  const raw = calculatorLootItemRateDisplay({
    state: "known",
    value: rate,
    basis: "rawWeight",
  });
  return Object.freeze({
    normalized: Object.freeze({
      state: normalized.state,
      text: `${normalized.text}%`,
      title: trimString(normalized.titleText),
    }),
    denormalized: Object.freeze({
      state: denormalized.state,
      text: denormalized.text,
      title: trimString(denormalized.titleText),
    }),
    raw: Object.freeze({
      state: raw.state,
      text: raw.text,
      title: trimString(raw.titleText),
    }),
  });
}

function hotspotRateDisplayFields(
  payload,
  effectiveText,
  ratePresentations = null,
) {
  const dropRateText = trimString(effectiveText);
  const normalizedPresentation = ratePresentations?.normalized ?? {
    state: ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN,
    text: "",
    title: "",
  };
  const denormalizedPresentation = ratePresentations?.denormalized ?? {
    state: ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN,
    text: "",
    title: "",
  };
  const rawPresentation = ratePresentations?.raw ?? {
    state: ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN,
    text: "",
    title: "",
  };
  const normalizedDropRateText = trimString(payload?.normalizedDropRateText) || dropRateText;
  const denormalizedDropRateText =
    trimString(payload?.denormalizedDropRateText) || denormalizedPresentation.text;
  const rawDropRateText = trimString(payload?.rawDropRateText) || rawPresentation.text;
  const normalizedDropRateState = parseZoneLootRateDisplayState(
    payload?.normalizedDropRateState
      ?? payload?.dropRateState
      ?? normalizedPresentation.state,
    "hotspot normalized rate state",
  );
  const denormalizedDropRateState = parseZoneLootRateDisplayState(
    payload?.denormalizedDropRateState ?? denormalizedPresentation.state,
    "hotspot denormalized rate state",
  );
  const rawDropRateState = parseZoneLootRateDisplayState(
    payload?.rawDropRateState ?? rawPresentation.state,
    "hotspot raw rate state",
  );
  const normalizedDropRateTitle =
    trimString(payload?.normalizedDropRateTitle)
    || (normalizedDropRateText === normalizedPresentation.text
      ? normalizedPresentation.title
      : "");
  const denormalizedDropRateTitle =
    trimString(payload?.denormalizedDropRateTitle)
    || (denormalizedDropRateText === denormalizedPresentation.text
      ? denormalizedPresentation.title
      : "");
  const rawDropRateTitle =
    trimString(payload?.rawDropRateTitle)
    || (rawDropRateText === rawPresentation.text ? rawPresentation.title : "");
  const rawDropRateBasis = trimString(payload?.rawDropRateBasis);
  const denormalizedDropRateBasis = trimString(payload?.denormalizedDropRateBasis);
  return {
    dropRateText,
    dropRateState: normalizedDropRateState,
    dropRateBasis: ZONE_LOOT_RATE_DISPLAY_BASIS_PERCENT,
    rawDropRateText,
    rawDropRateState,
    rawDropRateTitle,
    rawDropRateBasis: rawDropRateBasis
      ? parseZoneLootRateDisplayBasis(rawDropRateBasis, "hotspot raw rate basis")
      : ZONE_LOOT_RATE_DISPLAY_BASIS_RAW_WEIGHT,
    denormalizedDropRateText,
    denormalizedDropRateState,
    denormalizedDropRateTitle,
    denormalizedDropRateBasis: denormalizedDropRateBasis
      ? parseZoneLootRateDisplayBasis(
          denormalizedDropRateBasis,
          "hotspot denormalized rate basis",
        )
      : ZONE_LOOT_RATE_DISPLAY_BASIS_RAW_WEIGHT,
    normalizedDropRateText,
    normalizedDropRateState,
    ...(normalizedDropRateTitle ? { normalizedDropRateTitle } : {}),
    normalizedDropRateBasis: ZONE_LOOT_RATE_DISPLAY_BASIS_PERCENT,
  };
}

function hotspotGradeTone(gradeType) {
  switch (integerValue(gradeType)) {
    case 4:
      return "red";
    case 3:
      return "yellow";
    case 2:
      return "blue";
    case 1:
      return "green";
    case 0:
      return "white";
    default:
      return "unknown";
  }
}

const UNKNOWN_LOOT_SORT_VALUE = Object.freeze({ state: "unknown" });

function knownLootSortValue(value) {
  if (value === null || value === undefined || value === "") {
    return UNKNOWN_LOOT_SORT_VALUE;
  }
  const number = Number(value);
  return Number.isFinite(number) && number >= 0
    ? Object.freeze({ state: "known", value: number })
    : UNKNOWN_LOOT_SORT_VALUE;
}

function hotspotLootSortValues(payload, { itemId, label, databaseRank }) {
  const gradeType = integerValue(payload?.gradeType);
  const selectRate = Number(payload?.selectRate);
  return calculatorLootRowSortValues({
    price: knownLootSortValue(payload?.priceRaw),
    grade: knownLootSortValue(
      gradeType !== null && gradeType >= 0 && gradeType <= 4
        ? gradeType + 1
        : null,
    ),
    dropRate: knownLootSortValue(
      Number.isFinite(selectRate) && selectRate >= 0
        ? selectRate / 1_000_000
        : null,
    ),
    expectedValue: knownLootSortValue(payload?.expectedProfit),
    fishingExperience: knownLootSortValue(payload?.expectedFishingExperience),
    totemExperience: knownLootSortValue(payload?.expectedTotemExperience),
    evidence: knownLootSortValue(payload?.evidenceSortRank),
    database: knownLootSortValue(databaseRank),
    itemId: knownLootSortValue(itemId),
    label,
  });
}

function parseHotspotJsonPayload(value) {
  let payload = null;
  try {
    payload = JSON.parse(value);
  } catch (_error) {
    return null;
  }
  return isPlainObject(payload) ? payload : null;
}

function parseHotspotLootPayload(
  payload,
  keySuffix = "",
  {
    slotIdx = 0,
    groupLabel = "",
    catalogItems = [],
    databaseRank = 0,
  } = {},
) {
  if (!isPlainObject(payload)) {
    return null;
  }
  const itemId = integerValue(payload.itemId);
  const iconItemId = integerValue(payload.iconItemId);
  const label = trimString(payload.label) || trimString(payload.name);
  if (!itemId || !label) {
    return null;
  }
  const iconGradeTone = trimString(payload.iconGradeTone) || hotspotGradeTone(payload.gradeType);
  const ratePresentations = hotspotItemRatePresentations(payload.selectRate);
  const dropRateText = trimString(payload.dropRateText) || ratePresentations.normalized.text;
  return {
    key: `hotspot-loot:${keySuffix}:${itemId}:${trimString(payload.selectRate)}`,
    itemId,
    label,
    slotIdx: integerValue(payload.slotIdx) || slotIdx,
    groupLabel: trimString(payload.groupLabel) || groupLabel,
    iconUrl: itemId ? resolveFishItemIconUrl(itemId, {
      iconItemId,
      iconImage: payload.iconImage,
      catalogItems,
    }) : "",
    iconGradeTone,
    fillColor: trimString(payload.fillColor),
    strokeColor: trimString(payload.strokeColor),
    textColor: trimString(payload.textColor),
    rateClaims: normalizeZoneLootProvenanceClaims(
      payload.rateClaims,
      "hotspot.loot.rateClaims",
      "rowRate",
    ),
    presenceClaims: normalizeZoneLootProvenanceClaims(
      payload.presenceClaims,
      "hotspot.loot.presenceClaims",
      "rowPresence",
    ),
    ...hotspotRateDisplayFields(
      payload,
      dropRateText,
      ratePresentations,
    ),
    catchMethods: normalizeCatchMethods(payload.catchMethods),
    sortValues: hotspotLootSortValues(payload, { itemId, label, databaseRank }),
  };
}

function parseHotspotLootFact(fact, databaseRank, context = {}) {
  if (fact.key !== "loot_item") {
    return null;
  }
  return parseHotspotLootPayload(
    parseHotspotJsonPayload(fact.value),
    "",
    { ...context, databaseRank },
  );
}

function parseHotspotLootGroupFact(fact, index, context = {}) {
  if (fact.key !== "loot_group") {
    return null;
  }
  const payload = parseHotspotJsonPayload(fact.value);
  if (!payload) {
    return null;
  }
  const slotIdx = integerValue(payload.slotIdx) || index + 1;
  const label = trimString(payload.label) || `Group ${index + 1}`;
  const conditionOptions = (Array.isArray(payload.conditionOptions) ? payload.conditionOptions : [])
    .filter((option) => isPlainObject(option))
    .map((option, optionIndex) => {
      const rows = (Array.isArray(option.speciesRows) ? option.speciesRows : [])
        .map((row, rowIndex) =>
          parseHotspotLootPayload(
            row,
            `${trimString(option.conditionKey) || optionIndex}:${rowIndex}`,
            {
              slotIdx,
              groupLabel: label,
              catalogItems: context.catalogItems,
              databaseRank: rowIndex,
            },
          ),
        )
        .filter(Boolean);
      const optionDropRateText = trimString(option.dropRateText);
      return {
        conditionKey: trimString(option.conditionKey || option.conditionTooltip),
        conditions: normalizeZoneLootConditionSet(
          option.conditions,
          `hotspot.conditionOptions[${optionIndex}].conditions`,
        ),
        conditionText: trimString(option.conditionText),
        conditionTooltip: trimString(option.conditionTooltip),
        rateClaims: normalizeZoneLootProvenanceClaims(
          option.rateClaims,
          `hotspot.conditionOptions[${optionIndex}].rateClaims`,
          "groupRate",
        ),
        presenceClaims: normalizeZoneLootProvenanceClaims(
          option.presenceClaims,
          `hotspot.conditionOptions[${optionIndex}].presenceClaims`,
          "groupPresence",
        ),
        ...hotspotRateDisplayFields(option, optionDropRateText),
        active: option.active === true,
        speciesRows: rows,
      };
    })
    .filter((option) => option.conditionText || option.speciesRows.length > 0);
  if (!conditionOptions.length) {
    return null;
  }
  const groupDropRateText = trimString(payload.dropRateText);
  return {
    slotIdx,
    label,
    conditionOptionKey: trimString(payload.conditionOptionKey),
    fillColor: trimString(payload.fillColor),
    strokeColor: trimString(payload.strokeColor),
    textColor: trimString(payload.textColor),
    rateClaims: normalizeZoneLootProvenanceClaims(
      payload.rateClaims,
      "hotspot.group.rateClaims",
      "groupRate",
    ),
    presenceClaims: normalizeZoneLootProvenanceClaims(
      payload.presenceClaims,
      "hotspot.group.presenceClaims",
      "groupPresence",
    ),
    ...hotspotRateDisplayFields(payload, groupDropRateText),
    catchMethods: normalizeCatchMethods(payload.catchMethods),
    conditionOptions,
  };
}

function buildHotspotSection(
  layerSamples,
  conditionSelection = {},
  lootRateDisplayMode = "normalized",
  zoneLootSortBy = "price",
  zoneLootSortOrder = "desc",
  selectedModelTab = "",
  catalogItems = [],
) {
  const sample = layerSamples.find(isHotspotSample);
  const section = hotspotDetailSection(sample);
  if (!sample || !section) {
    return null;
  }
  const facts = detailSectionFacts(section);
  const fishName = firstFactValue(facts, "primary_fish") || trimString(sample?.targets?.[0]?.label);
  const fishItemId = integerValue(firstFactValue(facts, "primary_fish_item_id"));
  const hotspotId = firstFactValue(facts, "hotspot_id");
  const metadataSource = firstFactValue(facts, "metadata_source");
  const sourceMetadataStats = firstFactValue(facts, "source_metadata_stats");
  const modelFacts = buildHotspotModelFacts(facts);
  const modelViewer = buildHotspotModelViewer(facts, selectedModelTab);
  const target = Array.isArray(sample?.targets) ? sample.targets[0] : null;
  const worldX = Number(target?.worldX);
  const worldZ = Number(target?.worldZ);
  const focusAction = Number.isFinite(worldX) && Number.isFinite(worldZ)
    ? {
        kind: "focus-world-point",
        focusWorldPoint: {
          worldX,
          worldZ,
          elementKind: "hotspot",
          pointKind: "waypoint",
          pointLabel: trimString(sample?.targets?.[0]?.label) || fishName,
        },
      }
    : null;
  const metricRows = [
    hotspotMetric("Min. Catches", firstFactValue(facts, "min_fish_count"), ""),
    hotspotMetric("Max. Catches", firstFactValue(facts, "max_fish_count"), ""),
    hotspotMetric(
      "Catchable at",
      formatHotspotLifeLevel(firstFactValue(facts, "available_fishing_level")),
      "fish-fill",
    ),
    hotspotMetric(
      "Visible at",
      formatHotspotLifeLevel(firstFactValue(facts, "observe_fishing_level")),
      "eye",
    ),
  ];
  const minWaitTimeMs = firstFactValue(facts, "min_wait_time_ms");
  const maxWaitTimeMs = firstFactValue(facts, "max_wait_time_ms");
  const biteTime = {
    minimum: formatHotspotSeconds(minWaitTimeMs) || HOTSPOT_UNSET_STAT_VALUE,
    average: formatHotspotAverageSeconds(minWaitTimeMs, maxWaitTimeMs) || HOTSPOT_UNSET_STAT_VALUE,
    maximum: formatHotspotSeconds(maxWaitTimeMs) || HOTSPOT_UNSET_STAT_VALUE,
  };
  const lifetime = formatHotspotDuration(firstFactValue(facts, "point_remain_time_ms")) || HOTSPOT_UNSET_STAT_VALUE;
  const lootGroups = facts
    .filter((fact) => fact.key === "loot_group")
    .map((fact, index) => parseHotspotLootGroupFact(fact, index, { catalogItems }))
    .filter(Boolean);
  const lootRows = facts
    .map((fact, index) => parseHotspotLootFact(fact, index, { catalogItems }))
    .filter(Boolean);
  const fallbackLootRows = !lootRows.length && fishItemId && fishName
    ? [{
        key: `hotspot-loot:${fishItemId}`,
        itemId: fishItemId,
        label: fishName,
        iconUrl: resolveFishItemIconUrl(fishItemId, { catalogItems }),
        iconGradeTone: "unknown",
        dropRateText: "",
        sortValues: hotspotLootSortValues(
          {},
          { itemId: fishItemId, label: fishName, databaseRank: 0 },
        ),
      }]
    : [];
  const groups = lootGroups.length
    ? buildZoneLootGroups(
        {
          groups: lootGroups,
          speciesRows: [],
        },
        conditionSelection,
        lootRateDisplayMode,
        zoneLootSortBy,
        zoneLootSortOrder,
      )
    : (lootRows.length ? lootRows : fallbackLootRows).length
      ? [{
          label: "Group 1",
          rows: sortZoneLootRows(
            lootRows.length ? lootRows : fallbackLootRows,
            zoneLootSortBy,
            zoneLootSortOrder,
          ),
        }]
      : [];
  return {
    id: "hotspot-details",
    kind: "hotspot",
    title: "Hotspot Details",
    fishName,
    fishItemId,
    hotspotId,
    iconUrl: fishItemId ? resolveFishItemIconUrl(fishItemId, { catalogItems }) : "",
    focusAction,
    metadataSource,
    sourceMetadataStats,
    modelViewer,
    modelFacts,
    metrics: metricRows,
    biteTime,
    lifetime,
    groups,
    profiles: [],
  };
}

function normalizeDetailFacts(sample) {
  if (!Array.isArray(sample?.detailSections)) {
    return [];
  }
  const rows = [];
  for (const section of sample.detailSections) {
    if (!isPlainObject(section) || trimString(section.kind) !== "facts") {
      continue;
    }
    const sectionTitle = trimString(section.title);
    for (const fact of Array.isArray(section.facts) ? section.facts : []) {
      if (!isPlainObject(fact)) {
        continue;
      }
      const key = trimString(fact.key);
      const label = trimString(fact.label) || sectionTitle || key.replace(/_/g, " ");
      const value = trimString(fact.value);
      if (!key || !label || !value) {
        continue;
      }
      rows.push({
        key,
        label,
        value,
        icon: trimString(fact.icon),
        statusIcon: trimString(fact.statusIcon),
        statusIconTone: trimString(fact.statusIconTone),
      });
    }
  }
  return rows;
}

function isLandmarkSample(sample) {
  const layerId = trimString(sample?.layerId);
  const kind = trimString(sample?.kind);
  if (kind === "waypoint" || LANDMARK_LAYER_IDS.includes(layerId)) {
    return true;
  }
  return false;
}

function landmarkFactIcon(layerId, fact) {
  if (trimString(fact?.icon)) {
    return trimString(fact.icon);
  }
  if (isTradeNpcLayerId(layerId)) {
    return "trade-origin";
  }
  return "map-pin";
}

function buildLandmarkPaneFacts(layerSamples) {
  const facts = [];
  const seen = new Set();
  for (const sample of layerSamples.filter(isLandmarkSample)) {
    const layerId = trimString(sample?.layerId);
    for (const fact of normalizeDetailFacts(sample)) {
      const key = `${layerId}:${fact.key}:${fact.value}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      facts.push({
        key: `landmark:${layerId}:${fact.key}`,
        label: fact.label,
        value: fact.value,
        icon: landmarkFactIcon(layerId, fact),
        ...(fact.statusIcon ? { statusIcon: fact.statusIcon } : {}),
        ...(fact.statusIconTone ? { statusIconTone: fact.statusIconTone } : {}),
      });
    }
  }
  return facts;
}

function tradeNpcKeyFromSample(sample) {
  const npcKey = Number.parseInt(
    normalizeDetailFacts(sample).find((fact) => fact.key === "npc_key")?.value,
    10,
  );
  return Number.isInteger(npcKey) && npcKey > 0 ? npcKey : null;
}

function buildTradeNpcSection(selection, layerSamples, tradeNpcMapCatalog) {
  if (!["npc", "trade_npc"].includes(selectionTargetElementKind(selection))) {
    return null;
  }
  const sample = layerSamples.find((candidate) => isTradeNpcLayerId(candidate?.layerId));
  if (!sample) {
    return null;
  }
  const npcKey = tradeNpcKeyFromSample(sample);
  const npc = tradeNpcForKey(tradeNpcMapCatalog, npcKey);
  const npcName = trimString(npc?.npcName) ||
    trimString(
      normalizeDetailFacts(sample).find((fact) => fact.key === "trade_npc")?.value,
    );
  return {
    id: "trade-npc-details",
    kind: "trade-npc",
    npcKey,
    npcName,
    imageUrl: resolveTradeNpcImageUrl(npc),
    badgeImageUrl: resolveTradeNpcBadgeImageUrl(npc),
    class: npc?.class || "",
    classLabel: npc?.classLabel || "",
    regionLabel: trimString(npc?.spawnRegionLabel),
  };
}

function paneDescriptor(id, label, icon, summary, sections) {
  return {
    id,
    label,
    icon,
    summary: trimString(summary),
    sections: sections.filter(Boolean),
  };
}

export function patchTouchesInfoSignals(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  return Boolean(
    patch._map_runtime?.selection != null ||
      patch._map_runtime?.catalog?.layers != null ||
      patch._map_runtime?.catalog?.fish != null ||
      patch._map_ui?.windowUi?.zoneInfo != null ||
      patch._map_ui?.windowUi?.settings?.lootRateDisplayMode != null ||
      patch._map_ui?.windowUi?.settings?.zoneLootSortBy != null ||
      patch._map_ui?.windowUi?.settings?.zoneLootSortOrder != null ||
      patch._map_ui?.layers?.lineagePanelVisibleByLayer != null,
  );
}

export function buildInfoViewModel(
  signals,
  {
    zoneCatalog = [],
    zoneLootSummary = null,
    zoneLootStatus = "idle",
    zoneLootConditionSelection = {},
    lootRateDisplayMode = null,
    zoneLootSortBy = null,
    zoneLootSortOrder = null,
    tradeNpcMapCatalog = null,
    tradeNpcMapStatus = "idle",
  } = {},
) {
  const selection = isPlainObject(signals?._map_runtime?.selection)
    ? cloneJson(signals._map_runtime.selection)
    : {};
  const layerSamples = normalizeSelectionLayerSamples(selection);
  const runtimeLayers = Array.isArray(signals?._map_runtime?.catalog?.layers)
    ? cloneJson(signals._map_runtime.catalog.layers)
    : [];
  const runtimeFish = Array.isArray(signals?._map_runtime?.catalog?.fish)
    ? cloneJson(signals._map_runtime.catalog.fish)
    : [];
  const runtimeItems = Array.isArray(signals?._map_runtime?.catalog?.items)
    ? cloneJson(signals._map_runtime.catalog.items)
    : [];
  const pointSampleStateBundle = {
    state: {
      catalog: {
        fish: runtimeFish,
        items: runtimeItems,
      },
    },
  };
  const zoneFacts = buildZonePaneFacts(layerSamples, {
    zoneCatalog,
    runtimeLayers,
  });
  const landmarkFacts = buildLandmarkPaneFacts(layerSamples);
  const tradeNpcSection = buildTradeNpcSection(selection, layerSamples, tradeNpcMapCatalog);
  const territoryFacts = buildTerritoryPaneFacts(layerSamples, { runtimeLayers });
  const statusDescriptor = selectionStatusDescriptor(selection, layerSamples);
  const selectionTitle = titleFromSelection(
    selection,
    layerSamples,
    zoneCatalog,
    runtimeLayers,
  );
  const locationBookmarkSection = buildLocationBookmarkSection(selection);
  const tradeContent = buildTradePaneContent(layerSamples, {
    runtimeLayers,
    tradeNpcMapCatalog,
    tradeNpcMapStatus,
  });
  const tradeFacts = tradeContent.facts;
  const effectiveLootRateDisplayMode = parseCalculatorLootRateDisplayMode(
    lootRateDisplayMode ?? lootRateDisplayModeFromSignals(signals),
    "map info loot rate display mode",
  );
  const effectiveZoneLootSortBy =
    zoneLootSortBy == null
      ? zoneLootSortByFromSignals(signals)
      : resolveZoneLootSortBy(zoneLootSortBy);
  const effectiveZoneLootSortOrder =
    zoneLootSortOrder == null
      ? zoneLootSortOrderFromSignals(signals)
      : resolveZoneLootSortOrder(zoneLootSortOrder);
  const zoneLootSection = buildZoneLootSection(
    zoneLootSummary,
    zoneLootStatus,
    zoneLootConditionSelection,
    effectiveLootRateDisplayMode,
    effectiveZoneLootSortBy,
    effectiveZoneLootSortOrder,
    signals?._map_ui?.windowUi?.zoneInfo?.lootProfile,
  );
  const pointSampleSection = buildPointSampleSection(
    selection,
    pointSampleStateBundle,
    zoneCatalog,
  );
  const zoneMaskLineageSection = buildZoneMaskLineageSection(
    selection,
    signals?._map_ui?.layers?.lineagePanelVisibleByLayer?.zone_mask === true,
    zoneCatalog,
  );
  const hotspotSection = buildHotspotSection(
    layerSamples,
    zoneLootConditionSelection,
    effectiveLootRateDisplayMode,
    effectiveZoneLootSortBy,
    effectiveZoneLootSortOrder,
    signals?._map_ui?.windowUi?.zoneInfo?.hotspotModelTab,
    runtimeItems,
  );
  const characterIdentityKey = selectionTargetCharacterIdentityKey(selection);
  const characterActivitySection = characterIdentityKey
    ? {
        id: "character-activity",
        kind: "character-activity",
        title: "Character activity",
        identityKey: characterIdentityKey,
      }
    : null;
  const panes = [
    characterActivitySection
      ? paneDescriptor(
          "character",
          "Character",
          "user-4",
          selectionTargetPointLabel(selection),
          [characterActivitySection],
        )
      : null,
    pointSampleSection
      ? paneDescriptor(
          "samples",
          "Samples",
          "date-confirmed",
          pointSampleSection.rows[0]?.fishName || "",
          [pointSampleSection],
        )
      : null,
    hotspotSection
      ? paneDescriptor(
          "hotspot",
          "Hotspot",
          HOTSPOT_INFO_ICON,
          hotspotSection.fishName || "",
          [hotspotSection],
        )
      : null,
    paneDescriptor(
      "landmark",
      tradeNpcSection ? "NPC" : mapText("info.pane.landmark"),
      tradeNpcSection ? "trade-origin" : landmarkFacts[0]?.icon || "map-pin",
      tradeNpcSection?.npcName || landmarkFacts[0]?.value || "",
      tradeNpcSection
        ? [tradeNpcSection]
        : landmarkFacts.length
        ? [
            {
              id: "landmark-facts",
              kind: "facts",
              title: mapText("info.section.landmark"),
              facts: landmarkFacts,
            },
          ]
        : [],
    ),
    paneDescriptor(
      "zone",
      mapText("info.pane.zone"),
      "hover-zone",
      zoneFacts.find((fact) => fact.key === "zone")?.value || "",
      [
        zoneFacts.length
          ? {
              id: "zone-facts",
              kind: "facts",
              title: mapText("info.section.zone"),
              facts: zoneFacts,
            }
          : null,
        zoneLootSection,
      ],
    ),
    zoneMaskLineageSection
      ? paneDescriptor(
          "lineage",
          mapText("info.pane.lineage"),
          "inspect-fill",
          mapText("info.lineage.coordinate", {
            x: zoneMaskLineageSection.x,
            y: zoneMaskLineageSection.y,
          }),
          [zoneMaskLineageSection],
        )
      : null,
    paneDescriptor(
      "territory",
      mapText("info.pane.territory"),
      "hover-resources",
      territoryFacts[0]?.value || locationBookmarkSection?.coordinates || "",
      [
        territoryFacts.length
          ? {
              id: "territory-facts",
              kind: "facts",
              title: mapText("info.section.territory"),
              facts: territoryFacts,
            }
          : null,
        locationBookmarkSection,
      ],
    ),
    paneDescriptor(
      "trade",
      mapText("info.pane.trade"),
      "trade-origin",
      tradeFacts[0]?.value || "",
      [
        tradeFacts.length
          ? {
              id: "trade-facts",
              kind: "facts",
              title: mapText("info.section.trade"),
              facts: tradeFacts,
            }
          : null,
        tradeContent.managerGroups.length
          ? {
              id: "trade-managers",
              kind: "trade-managers",
              title: mapText("info.trade.managers"),
              groups: tradeContent.managerGroups,
            }
          : null,
      ].filter(Boolean),
    ),
  ].filter((pane) => pane && pane.sections.length > 0);

  const requestedPaneId = trimString(signals?._map_ui?.windowUi?.zoneInfo?.tab);
  const preferredPaneId = preferredPaneIdFromSelection(selection);
  const fallbackPaneId = paneIdExists(panes, preferredPaneId)
    ? preferredPaneId
    : panes[0]?.id || "";
  const activePaneId =
    paneIdExists(panes, requestedPaneId)
      ? requestedPaneId
      : fallbackPaneId;
  const pointKind = normalizePointKind(selection?.pointKind);
  const overviewRows = buildOverviewRowsForLayerSamples(layerSamples, {
    zoneCatalog,
    runtimeLayers,
  });
  return {
    descriptor: {
      title: selectionTitle,
      titleIcon: INFO_WINDOW_TITLE_ICON,
      statusIcon: statusDescriptor.statusIcon,
      statusText: statusDescriptor.statusText,
      pointKind,
      overviewRows,
    },
    panes,
    activePaneId,
    activePane: panes.find((pane) => pane.id === activePaneId) || null,
    empty: panes.length === 0,
  };
}
